import { Link, Stack, Typography } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { useRetryTranscript, useRetrySummary } from '../../../../resources/phoning/mutations';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

type ProcessingFailedNotificationProps = {
    transcriptErrorCode: string | null;
    summaryErrorCode: string | null;
    dossierId: string | null;
    dossierPhoneCallId: string | null;
    dismissNotification: () => void;
};

export const ProcessingFailedNotification = ({
    transcriptErrorCode,
    summaryErrorCode,
    dossierId,
    dossierPhoneCallId,
    dismissNotification,
}: ProcessingFailedNotificationProps) => {
    const { formatMessage } = useIntl();

    const retryTranscript = useRetryTranscript();
    const retrySummary = useRetrySummary();

    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId ?? '');

    const isTranscriptError = Boolean(transcriptErrorCode);
    const isSummaryError = Boolean(summaryErrorCode);

    const messageId =
        isSummaryError || (isTranscriptError && isSummaryError)
            ? 'experiment.better_business.dossier.discussion.phone_call.notification.error_summary_gen'
            : 'experiment.better_business.dossier.discussion.phone_call.notification.error_transcript_gen';

    const handleRetry = () => {
        if (!dossierId || !dossierPhoneCallId) return;

        trackEvent((ampli, defaultProperties) => {
            ampli.regenerateRecordingAiContentClicked({
                ...defaultProperties,
                ...trackedTripProperties,
                source_regeneration: isTranscriptError
                    ? 'recording raw transcript'
                    : 'recording summary',
            });
        });

        if (isTranscriptError) {
            retryTranscript.mutate({ dossierId, dossierPhoneCallId });
        } else if (isSummaryError) {
            retrySummary.mutate({ dossierId, dossierPhoneCallId });
        }

        dismissNotification();
    };

    return (
        <Stack direction="column" spacing={1}>
            <Typography variant="body2" fontFamily="inherit" color="inherit" fontWeight={700}>
                {formatMessage({ id: messageId })}
            </Typography>
            <Link
                component="button"
                variant="body2"
                onClick={handleRetry}
                disabled={!dossierId || !dossierPhoneCallId}
                sx={{ textAlign: 'left', fontFamily: 'inherit', fontWeight: 'bold' }}
            >
                {formatMessage({
                    id: 'dossier.discussion.phone_call.notification.retry_generation',
                })}
            </Link>
        </Stack>
    );
};
